import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EyeOutlined,
  EditOutlined,
  SearchOutlined,
  PlusOutlined,
  GlobalOutlined
} from "@ant-design/icons";
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Table,
  Typography,
  notification,
  Card,
  Statistic,
  Switch
} from "antd";
import { useEffect, useState, useCallback, useRef } from "react";
import ajaxService from "../../../services/ajax-service";
import { Link } from "react-router-dom";
import moment from "moment";
import { calculateTableScrollX, calculateModalWidth } from "../../../utils/widthCalculator";
import FlagSelector, { getEmojiFlag } from "../../../components/FlagSelector";
import FlagDisplay from "../../../components/FlagDisplay";

const { Text, Title } = Typography;

const OriginCountries = () => {
  const [form] = Form.useForm();
  const countryNameValue = Form.useWatch('name', form);

  const [showModal, setShowModal] = useState(false);

  const [dataSource, setDataSource] = useState({ data: [], current_page: 1, per_page: 10, total: 0 });
  const [additionalData, setAdditionalData] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    this_month: 0
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const debounceTimer = useRef(null);

  const [editingCountry, setEditingCountry] = useState(null);

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [countryToDelete, setCountryToDelete] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [modalWidth, setModalWidth] = useState('35%');

  const pageSize = 10;

  const columns = [
    {
      title: "SNo",
      dataIndex: "id",
      key: "id",
      width: 70,
      fixed: 'left',
      render: (text, record, index) => (
        <Text style={{ fontSize: '0.85rem' }}>{(dataSource.current_page - 1) * dataSource.per_page + index + 1}</Text>
      ),
    },
    {
      title: "Flag",
      dataIndex: "flag",
      key: "flag",
      width: 80,
      render: (flag, record) => (
        <FlagDisplay flag={flag} isoCode={record.iso_3166_alpha2} size={32} />
      ),
    },
    {
      title: "Country",
      dataIndex: "name",
      key: "name",
      width: 300,
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (name) => <Text style={{ fontSize: '0.85rem' }}>{name}</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => (
        <span style={{ 
          color: status ? 'var(--success-green)' : 'var(--error-red)',
          fontSize: '0.85rem'
        }}>
          {status ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 110,
      sorter: (a, b) => (moment(a.created_at).unix() || 0) - (moment(b.created_at).unix() || 0),
      sortDirections: ['ascend', 'descend'],
      render: (created_at) => (<Text style={{ fontSize: '0.8rem' }}>{moment(created_at).format('DD/MM/YY')}</Text>)
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      fixed: 'right',
      render: (text, record) => (
        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
          <Link to={`/origin-countries/${record.id}`}>
            <Button
              type="text"
              size="small"
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                color: 'var(--primary-purple)',
                border: '1px solid var(--primary-purple)',
                borderRadius: 'var(--radius-md)',
                transition: 'var(--transition-fast)'
              }}
              icon={<EyeOutlined style={{ fontSize: '12px' }} />}
            />
          </Link>
          <Button
            type="text"
            size="small"
            style={{
              padding: '4px 8px',
              fontSize: '12px',
              color: 'var(--accent-purple)',
              border: '1px solid var(--accent-purple)',
              borderRadius: 'var(--radius-md)',
              transition: 'var(--transition-fast)'
            }}
            icon={<EditOutlined style={{ fontSize: '12px' }} />}
            onClick={() => openEditModal(record)}
          />
          <Button
            type="text"
            size="small"
            style={{
              padding: '4px 8px',
              fontSize: '12px',
              color: 'var(--error-red)',
              border: '1px solid var(--error-red)',
              borderRadius: 'var(--radius-md)',
              transition: 'var(--transition-fast)'
            }}
            icon={<DeleteOutlined style={{ fontSize: '12px' }} />}
            onClick={() => handleDeleteClick(record.id)}
          />
        </div>
      ),
    },
  ];

  const handleDeleteClick = (id) => {
    setCountryToDelete(id);
    setDeleteModalVisible(true);
  };

  const handleConfirmDelete = async () => {
    if (countryToDelete) {
      try {
        const response = await ajaxService.delete('/origin-countries/', countryToDelete);
        if (response.success) {
          notification.open({
            message: `Origin country deleted successfully!`,
            icon: <CheckCircleOutlined style={{ color: "var(--success-green)" }} />,
          });
          initialize();
        } else {
          notification.open({
            message: "Unable to delete origin country.",
            icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
          });
        }
      } catch (error) {
        console.error('Delete error:', error);
        notification.open({
          message: "Unable to delete origin country.",
          icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
        });
      }
    }
    setDeleteModalVisible(false);
    setCountryToDelete(null);
  };

  const onFinish = async (values) => {
    let response;
    const payload = {
      name: values.name,
      flag: getEmojiFlag(values.iso_3166_alpha2),
      iso_3166_alpha2: values.iso_3166_alpha2,
      status: values.status ? 1 : 0,
    };

    if (editingCountry) {
      response = await ajaxService.put('/origin-countries/', editingCountry.id, payload);
    } else {
      response = await ajaxService.post("/origin-countries", payload);
    }

    if (response.success) {
      handleCancel();
      initialize();
      notification.open({
        message: response.message || (editingCountry ? "Origin country updated successfully!" : "Origin country added successfully!"),
        icon: <CheckCircleOutlined style={{ color: "var(--primary-purple)" }} />,
      });
    } else {
      notification.open({
        message: "Unable to save origin country.",
        icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
      });
    }
  };

  const openEditModal = (record) => {
    setEditingCountry(record);
    setShowModal(true);
    form.setFieldsValue({
      name: record.name,
      iso_3166_alpha2: record.iso_3166_alpha2,
      status: record.status,
    });
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed: " + errorInfo);
  };

  const handleSearch = async ({ name }) => {
    const query = name ? name.trim() : '';
    setSearchQuery(query);
    setCurrentPage(1);
    
    const url = query ? `/origin-countries?name=${encodeURIComponent(query)}` : "/origin-countries";
    const response = await ajaxService.get(url);
    
    if (response.success) {
      const countriesData = response.data?.data || [];
      const paginationData = response.data || {};
      
      setDataSource({
        data: countriesData,
        current_page: paginationData.current_page || 1,
        per_page: paginationData.per_page || pageSize,
        total: paginationData.total || countriesData.length
      });
      
      if (response.additionalData) {
        setAdditionalData({
          total: response.additionalData.total || 0,
          active: response.additionalData.active || 0,
          inactive: response.additionalData.inactive || 0,
          this_month: response.additionalData.this_month || 0,
        });
      }
      
      if (countriesData.length === 0 && query) {
        notification.open({
          message: `No countries found matching "${query}"`,
          icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
        });
      }
    }
  };

  // Debounced search effect
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      handleSearch({ name: searchValue });
    }, 500);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchValue]);

  const handleChangePage = async (page) => {
    setCurrentPage(page);
    const url = searchQuery 
      ? `/origin-countries?name=${encodeURIComponent(searchQuery)}&page=${page}` 
      : `/origin-countries?page=${page}`;
    
    const response = await ajaxService.get(url);
    if (response.success) {
      const countriesData = response.data?.data || [];
      const paginationData = response.data || {};
      
      setDataSource({
        data: countriesData,
        current_page: paginationData.current_page || page,
        per_page: paginationData.per_page || pageSize,
        total: paginationData.total || countriesData.length
      });
    }
  };

  const initialize = async () => {
    const response = await ajaxService.get("/origin-countries");
    if (response.success) {
      const countriesData = response.data?.data || [];
      const paginationData = response.data || {};
      
      setCurrentPage(1);
      setDataSource({
        data: countriesData,
        current_page: paginationData.current_page || 1,
        per_page: paginationData.per_page || pageSize,
        total: paginationData.total || countriesData.length
      });
      
      if (response.additionalData) {
        setAdditionalData({
          total: response.additionalData.total || 0,
          active: response.additionalData.active || 0,
          inactive: response.additionalData.inactive || 0,
          this_month: response.additionalData.this_month || 0,
        });
      }
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  useEffect(() => {
    const handleSidebarCollapse = (event) => {
      setSidebarCollapsed(event.detail.collapsed);
    };

    window.addEventListener('sidebarCollapse', handleSidebarCollapse);
    return () => window.removeEventListener('sidebarCollapse', handleSidebarCollapse);
  }, []);

  const calculateTableWidth = useCallback(() => {
    return calculateTableScrollX(sidebarCollapsed, 850, 850, 40);
  }, [sidebarCollapsed]);

  const [tableWidth, setTableWidth] = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      return calculateTableScrollX(false, 850, 850, 40);
    }
    return '100%';
  });

  useEffect(() => {
    const handleResize = () => {
      setTableWidth(calculateTableWidth());
      setModalWidth(calculateModalWidth('35%'));
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateTableWidth]);

  const handleCancel = () => {
    setShowModal(false);
    setEditingCountry(null);
    form.resetFields();
  };

  const renderModal = (
    <Modal
      className="modal-overlay"
      width={modalWidth}
      open={showModal}
      maskClosable={false}
      onCancel={handleCancel}
      footer={null}
      centered
    >
      <div style={{ 
        padding: "var(--spacing-2xl) var(--spacing-xl)",
        background: 'var(--dark-bg-card)',
        borderRadius: 'var(--radius-lg)'
      }}>
        <div style={{ 
          marginBottom: 'var(--spacing-2xl)', 
          textAlign: "center",
          paddingBottom: 'var(--spacing-lg)',
          borderBottom: '1px solid rgba(254, 2, 125, 0.2)'
        }}>
          <Title id="modal-title" level={3} style={{ 
            color: 'var(--text-white)', 
            margin: 0,
            fontFamily: 'Poppins-Bold',
            fontSize: '1.5rem'
          }}>
            {editingCountry ? "Edit Origin Country" : "Add New Origin Country"}
          </Title>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          initialValues={{ status: true }}
        >
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item
                label={<span style={{ 
                  color: 'var(--text-white)', 
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  display: 'block',
                  marginBottom: 'var(--spacing-xs)'
                }}>Country Name</span>}
                name="name"
                rules={[{ required: true, message: "Country name is required" }]}
              >
                <Input
                  placeholder="Enter country name"
                  allowClear
                  className="input-field"
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item
                label={<span style={{
                  color: 'var(--text-white)',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  display: 'block',
                  marginBottom: 'var(--spacing-xs)'
                }}>Country Flag</span>}
                name="iso_3166_alpha2"
              >
                <FlagSelector
                  countryName={countryNameValue}
                  onSelect={(country) => {
                    form.setFieldsValue({
                      iso_3166_alpha2: country.code
                    });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item
                label={<span style={{ 
                  color: 'var(--text-white)', 
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  display: 'block',
                  marginBottom: 'var(--spacing-xs)'
                }}>Status</span>}
                name="status"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="Active"
                  unCheckedChildren="Inactive"
                  style={{ backgroundColor: 'var(--primary-purple)' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ 
            textAlign: "center", 
            marginTop: 'var(--spacing-2xl)',
            paddingTop: 'var(--spacing-lg)',
            borderTop: '1px solid rgba(254, 2, 125, 0.2)',
            display: 'flex',
            gap: 'var(--spacing-md)',
            justifyContent: 'center'
          }}>
            <Button
              key="cancel"
              onClick={handleCancel}
              style={{
                minWidth: '120px',
                height: '40px',
                background: 'var(--dark-bg-hover)',
                borderColor: 'rgba(254, 2, 125, 0.3)',
                color: 'var(--text-white)'
              }}
            >
              Cancel
            </Button>
            <Button
              key="finish"
              type="primary"
              onClick={form.submit}
              className="btn-primary"
              style={{ 
                minWidth: '120px',
                height: '40px'
              }}
            >
              {editingCountry ? "Update" : "Add"}
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );

  return (
    <div className={`page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <div className="module-header">
        <h1 className="module-title">Origin Country Management</h1>
        <p className="module-subtitle">Manage origin countries for user profiles</p>
      </div>

      <div className="stats-grid">
        <div className="stats-card">
          <div className="stats-value">{additionalData.total ?? 0}</div>
          <div className="stats-label">Total Countries</div>
          <GlobalOutlined style={{ color: 'var(--primary-purple)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
        </div>
        <div className="stats-card">
          <div className="stats-value">{additionalData.active ?? 0}</div>
          <div className="stats-label">Active</div>
          <GlobalOutlined style={{ color: 'var(--success-green)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
        </div>
        <div className="stats-card">
          <div className="stats-value">{additionalData.inactive ?? 0}</div>
          <div className="stats-label">Inactive</div>
          <GlobalOutlined style={{ color: 'var(--error-red)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
        </div>
        <div className="stats-card">
          <div className="stats-value">{additionalData.this_month ?? 0}</div>
          <div className="stats-label">This Month</div>
          <GlobalOutlined style={{ color: 'var(--accent-purple)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
        </div>
      </div>

      <div className="filter-section">
        <div className="filter-row">
          <div className="filter-item">
            <Input
              placeholder="Search countries..."
              prefix={<SearchOutlined style={{ color: 'var(--text-gray-light)' }} />}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onPressEnter={(e) => handleSearch({ name: e.target.value })}
              allowClear
              onClear={() => {
                setSearchValue('');
                handleSearch({ name: '' });
              }}
              className="input-field"
            />
          </div>
          <div className="filter-actions">
            <Button
              className="btn-primary"
              icon={<PlusOutlined />}
              onClick={() => setShowModal(true)}
            >
              Add New Country
            </Button>
          </div>
        </div>
      </div>

      <div className={`data-table-card ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Table
          columns={columns}
          dataSource={dataSource.data || []}
          className="data-table"
          rowKey="id"
          pagination={{
            onChange: handleChangePage,
            current: dataSource.current_page,
            pageSize: dataSource.per_page,
            total: dataSource.total,
            className: "data-table-pagination",
            showSizeChanger: false,
            showQuickJumper: false,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} countries`,
            size: 'small'
          }}
          scroll={{ x: tableWidth }}
          size="small"
          bordered={false}
        />
      </div>

      {renderModal}
      <Modal
        className="modal-overlay"
        width={calculateModalWidth('400px')}
        open={deleteModalVisible}
        onCancel={() => setDeleteModalVisible(false)}
        footer={null}
        centered
      >
        <div style={{ 
          padding: "var(--spacing-2xl)",
          background: 'var(--dark-bg-card)',
          borderRadius: 'var(--radius-lg)',
          textAlign: 'center'
        }}>
          <div style={{ 
            marginBottom: 'var(--spacing-xl)',
            fontSize: '3rem',
            color: 'var(--error-red)'
          }}>
            <DeleteOutlined />
          </div>
          <Title level={4} style={{ 
            color: 'var(--text-white)', 
            marginBottom: 'var(--spacing-md)',
            fontFamily: 'Poppins-Bold'
          }}>
            Confirm Delete
          </Title>
          <p style={{ 
            color: 'var(--text-gray)', 
            marginBottom: 'var(--spacing-xl)',
            fontSize: '0.95rem',
            lineHeight: 1.6
          }}>
            Are you sure you want to delete this origin country? This action cannot be undone.
          </p>
          <div style={{ 
            display: 'flex',
            gap: 'var(--spacing-md)',
            justifyContent: 'center'
          }}>
            <Button
              onClick={() => setDeleteModalVisible(false)}
              style={{
                minWidth: '120px',
                height: '40px',
                background: 'var(--dark-bg-hover)',
                borderColor: 'rgba(254, 2, 125, 0.3)',
                color: 'var(--text-white)'
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              danger
              onClick={handleConfirmDelete}
              style={{
                minWidth: '120px',
                height: '40px',
                background: 'var(--error-red)',
                borderColor: 'var(--error-red)'
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OriginCountries;
